import { useEffect, useState } from "react";
import { Plus, Edit, Trash2, X, Save, Star } from "lucide-react";
import api, { resolveImage } from "@/lib/api";
import { toast, Toaster } from "sonner";
import ImageUpload from "@/components/ImageUpload";

const empty = {
  name: "",
  category_slug: "",
  short_description: "",
  description: "",
  image_url: "",
  specs: [],
  is_published: true,
  is_featured: false,
};

export default function AdminProducts() {
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [editing, setEditing] = useState(null); // null=closed, {} new, {...} edit
  const [form, setForm] = useState(empty);

  const load = async () => {
    const [p, c] = await Promise.all([
      api.get("/admin/products"),
      api.get("/admin/categories"),
    ]);
    setItems(p.data || []);
    setCategories(c.data || []);
  };

  useEffect(() => { load(); }, []);

  const open = (item) => {
    if (item) {
      setForm({ ...empty, ...item, specs: item.specs || [] });
      setEditing(item);
    } else {
      setForm({ ...empty, category_slug: categories[0]?.slug || "" });
      setEditing({});
    }
  };

  const save = async () => {
    try {
      if (!form.name || !form.category_slug) {
        toast.error("Ad ve kategori zorunlu");
        return;
      }
      if (editing.id) {
        await api.put(`/admin/products/${editing.id}`, form);
        toast.success("Ürün güncellendi");
      } else {
        await api.post("/admin/products", form);
        toast.success("Ürün eklendi");
      }
      setEditing(null);
      load();
    } catch (err) {
      toast.error("Kaydetme hatası");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Bu ürünü silmek istediğinize emin misiniz?")) return;
    await api.delete(`/admin/products/${id}`);
    toast.success("Silindi");
    load();
  };

  const updateSpec = (i, key, val) => {
    const specs = [...form.specs];
    specs[i] = { ...specs[i], [key]: val };
    setForm({ ...form, specs });
  };
  const addSpec = () => setForm({ ...form, specs: [...form.specs, { key: "", value: "" }] });
  const removeSpec = (i) => setForm({ ...form, specs: form.specs.filter((_, idx) => idx !== i) });

  return (
    <div data-testid="admin-products-page">
      <Toaster position="top-right" />
      <div className="flex items-center justify-between mb-8">
        <h1 className="heading-font text-3xl text-[#0A2540]">Ürünler</h1>
        <button
          onClick={() => open(null)}
          className="inline-flex items-center gap-2 bg-[#0066FF] hover:bg-[#0A2540] text-white px-4 py-2.5 text-sm font-semibold transition-colors"
          data-testid="admin-product-add"
        >
          <Plus size={16} /> Yeni Ürün
        </button>
      </div>

      <div className="bg-white border border-slate-200 overflow-x-auto">
        <table className="w-full text-sm min-w-[600px]">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              <th className="text-left px-6 py-3 text-xs uppercase tracking-wider text-slate-500">Ürün</th>
              <th className="text-left px-6 py-3 text-xs uppercase tracking-wider text-slate-500">Kategori</th>
              <th className="text-left px-6 py-3 text-xs uppercase tracking-wider text-slate-500">Durum</th>
              <th className="text-right px-6 py-3 text-xs uppercase tracking-wider text-slate-500">İşlem</th>
            </tr>
          </thead>
          <tbody>
            {items.map((p) => (
              <tr key={p.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`admin-product-row-${p.slug}`}>
                <td className="px-6 py-3">
                  <div className="flex items-center gap-3">
                    {p.image_url && <img src={resolveImage(p.image_url)} alt="" className="w-10 h-10 object-cover" />}
                    <div>
                      <div className="font-medium text-[#0A2540] flex items-center gap-2">
                        {p.name}
                        {p.is_featured && <Star size={12} className="text-[#0066FF] fill-[#0066FF]" />}
                      </div>
                      <div className="text-xs text-slate-500">{p.slug}</div>
                    </div>
                  </div>
                </td>
                <td className="px-6 py-3 text-slate-600">
                  {categories.find((c) => c.slug === p.category_slug)?.name || p.category_slug}
                </td>
                <td className="px-6 py-3">
                  <span className={`text-xs px-2 py-1 ${p.is_published ? "bg-green-50 text-green-700" : "bg-slate-100 text-slate-500"}`}>
                    {p.is_published ? "Yayında" : "Taslak"}
                  </span>
                </td>
                <td className="px-6 py-3 text-right">
                  <button onClick={() => open(p)} className="text-[#0066FF] hover:underline text-xs mr-3" data-testid={`admin-product-edit-${p.slug}`}>
                    <Edit size={14} className="inline mr-1" /> Düzenle
                  </button>
                  <button onClick={() => remove(p.id)} className="text-red-600 hover:underline text-xs" data-testid={`admin-product-delete-${p.slug}`}>
                    <Trash2 size={14} className="inline mr-1" /> Sil
                  </button>
                </td>
              </tr>
            ))}
            {items.length === 0 && (
              <tr><td colSpan={4} className="px-6 py-12 text-center text-slate-500">Henüz ürün yok</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {editing !== null && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="admin-product-modal">
          <div className="bg-white max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 sticky top-0 bg-white z-10">
              <h2 className="heading-font text-xl text-[#0A2540]">{editing.id ? "Ürünü Düzenle" : "Yeni Ürün"}</h2>
              <button onClick={() => setEditing(null)} data-testid="admin-product-modal-close"><X size={20} /></button>
            </div>
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs uppercase tracking-wider text-slate-500 mb-2">Ürün Adı *</label>
                  <input type="text" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full px-4 py-3 border border-slate-200 focus:border-[#0066FF] outline-none" data-testid="product-form-name" />
                </div>
                <div>
                  <label className="block text-xs uppercase tracking-wider text-slate-500 mb-2">Kategori *</label>
                  <select value={form.category_slug} onChange={(e) => setForm({ ...form, category_slug: e.target.value })} className="w-full px-4 py-3 border border-slate-200 focus:border-[#0066FF] outline-none bg-white" data-testid="product-form-category">
                    <option value="">Seçiniz...</option>
                    {categories.map((c) => <option key={c.id} value={c.slug}>{c.name}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <ImageUpload
                  value={form.image_url}
                  onChange={(url) => setForm({ ...form, image_url: url })}
                  label="Ürün Görseli"
                  testId="product-image-upload"
                />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-wider text-slate-500 mb-2">Kısa Açıklama</label>
                <input type="text" value={form.short_description} onChange={(e) => setForm({ ...form, short_description: e.target.value })} className="w-full px-4 py-3 border border-slate-200 focus:border-[#0066FF] outline-none" data-testid="product-form-short-desc" />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-wider text-slate-500 mb-2">Detaylı Açıklama</label>
                <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={4} className="w-full px-4 py-3 border border-slate-200 focus:border-[#0066FF] outline-none resize-none" data-testid="product-form-description" />
              </div>
              <div>
                <div className="flex items-center justify-between mb-3">
                  <label className="text-xs uppercase tracking-wider text-slate-500">Teknik Özellikler</label>
                  <button onClick={addSpec} className="text-xs text-[#0066FF] hover:underline" data-testid="product-form-add-spec">+ Özellik Ekle</button>
                </div>
                <div className="space-y-2">
                  {form.specs.map((s, i) => (
                    <div key={i} className="flex gap-2">
                      <input type="text" value={s.key} onChange={(e) => updateSpec(i, "key", e.target.value)} placeholder="Özellik (örn: pH)" className="flex-1 px-3 py-2 border border-slate-200 focus:border-[#0066FF] outline-none text-sm" />
                      <input type="text" value={s.value} onChange={(e) => updateSpec(i, "value", e.target.value)} placeholder="Değer" className="flex-1 px-3 py-2 border border-slate-200 focus:border-[#0066FF] outline-none text-sm" />
                      <button onClick={() => removeSpec(i)} className="px-3 text-red-600 hover:bg-red-50"><X size={16} /></button>
                    </div>
                  ))}
                </div>
              </div>
              <div className="flex items-center gap-6">
                <label className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={form.is_published} onChange={(e) => setForm({ ...form, is_published: e.target.checked })} data-testid="product-form-published" />
                  Yayında
                </label>
                <label className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={form.is_featured} onChange={(e) => setForm({ ...form, is_featured: e.target.checked })} data-testid="product-form-featured" />
                  Öne Çıkan
                </label>
              </div>
            </div>
            <div className="px-6 py-4 border-t border-slate-200 bg-slate-50 flex justify-end gap-2">
              <button onClick={() => setEditing(null)} className="px-5 py-2.5 border border-slate-300 hover:bg-white text-sm">İptal</button>
              <button onClick={save} className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#0066FF] hover:bg-[#0A2540] text-white text-sm font-semibold transition-colors" data-testid="product-form-save">
                <Save size={14} /> Kaydet
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
